(() => {
  if (window.MistaraMode?.version === 4) return;

  const DEFAULT_MODE = 'kadak';
  const STORAGE_KEYS = ['mistara-preferred-mode', 'mistara-selected-mode', 'mistara-mode'];
  const MODE_EVENT = 'mistara:mode-change';
  const TRIGGER_SELECTOR = [
    '[data-mistara-mode-trigger]',
    '[data-mode-button]',
    '[data-mistara-mode-button]',
    '[data-mtm-choice]',
  ].join(', ');
  const CONTROL_SELECTOR = TRIGGER_SELECTOR;

  const normalizeMode = (value) => {
    const label = String(value ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
    return /^(kadak|restart)(?:\s+mode)?$/.exec(label)?.[1] || null;
  };

  const getStoredMode = () => {
    try {
      for (const key of STORAGE_KEYS) {
        const mode = normalizeMode(window.localStorage.getItem(key));
        if (mode) return mode;
      }
    } catch (_error) {}
    return null;
  };

  const storeMode = (mode) => {
    try {
      STORAGE_KEYS.forEach((key) => window.localStorage.setItem(key, mode));
    } catch (_error) {}
  };

  const getTriggerMode = (trigger) => {
    // Shelf filters and card links belong to the section's local state.
    if (trigger.closest('[data-mistara-shelf]')) return null;
    const explicitMode = trigger.dataset.mistaraModeTrigger || trigger.dataset.modeButton ||
      trigger.dataset.mistaraModeButton || trigger.dataset.mtmChoice;
    return normalizeMode(explicitMode);
  };

  const updateModeContent = (mode) => {
    document.documentElement.dataset.mistaraMode = mode;
    document.documentElement.classList.toggle('mistara-mode-kadak', mode === 'kadak');
    document.documentElement.classList.toggle('mistara-mode-restart', mode === 'restart');

    document.querySelectorAll('[data-mistara-mode-root], [data-mistara-hero]').forEach((element) => {
      element.dataset.mode = mode;
    });
    document.querySelectorAll('mistara-two-modes[id^="MistaraTwoModes-"]').forEach((element) => {
      element.dataset.activeMode = mode;
    });
    document.querySelectorAll('[data-mistara-mode-content]').forEach((element) => {
      const isCurrentMode = normalizeMode(element.dataset.mistaraModeContent) === mode;
      element.hidden = !isCurrentMode;
      element.setAttribute('aria-hidden', String(!isCurrentMode));
    });
    document.querySelectorAll(CONTROL_SELECTOR).forEach((trigger) => {
      const triggerMode = getTriggerMode(trigger);
      if (!triggerMode) return;
      const isCurrentMode = triggerMode === mode;
      trigger.classList.toggle('is-active', isCurrentMode);
      trigger.setAttribute('aria-pressed', String(isCurrentMode));
    });

    const announcementSlides = Array.from(document.querySelectorAll('.announcement-bar__slide'));
    if (announcementSlides.length > 0) {
      let activeFound = false;
      announcementSlides.forEach((slide) => {
        const slideMode = (slide.dataset.mode || slide.dataset.mistaraModeContent || '').trim().toLowerCase();
        const matchesMode = (slideMode === mode || slideMode === 'all');
        if (matchesMode && !activeFound) {
          slide.hidden = false;
          slide.removeAttribute('hidden');
          slide.setAttribute('aria-hidden', 'false');
          activeFound = true;
        } else {
          slide.hidden = true;
          slide.setAttribute('hidden', '');
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    }
  };

  const setMode = (requestedMode, options = {}) => {
    const mode = normalizeMode(requestedMode);
    if (!mode) return;
    const previousMode = window.MistaraMode.current;
    window.MistaraMode.current = mode;
    updateModeContent(mode);
    if (options.remember !== false) storeMode(mode);
    if (previousMode !== mode || options.forceEvent) {
      document.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: { mode, previousMode } }));
    }
  };

  const refresh = () => updateModeContent(window.MistaraMode.current);
  window.MistaraMode = {
    version: 4,
    current: getStoredMode() || normalizeMode(document.documentElement.dataset.mistaraMode) || DEFAULT_MODE,
    set: setMode,
    get: () => window.MistaraMode.current,
    refresh,
  };
  window.setMistaraMode = setMode;

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest(CONTROL_SELECTOR);
    if (!trigger) return;
    const mode = getTriggerMode(trigger);
    if (mode) setMode(mode);
  });

  document.addEventListener('keydown', (event) => {
    if (!(event.target instanceof Element) || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    const trigger = event.target.closest('mistara-two-modes[id^="MistaraTwoModes-"] [data-mtm-choice]');
    if (trigger) setMode(trigger.closest('mistara-two-modes').dataset.activeMode);
  });

  const receiveMode = (event) => {
    const mode = normalizeMode(event.detail?.mode);
    if (mode && (mode !== window.MistaraMode.current || mode !== document.documentElement.dataset.mistaraMode)) setMode(mode);
  };
  document.addEventListener(MODE_EVENT, receiveMode);
  window.addEventListener(MODE_EVENT, receiveMode);

  const modeObserver = new MutationObserver(() => {
    const mode = normalizeMode(document.documentElement.dataset.mistaraMode);
    if (mode && mode !== window.MistaraMode.current) setMode(mode);
  });
  modeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-mistara-mode'] });

  const initialize = () => setMode(window.MistaraMode.current, { remember: false });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:block:select', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
})();
